import { useState } from "react";
import { redirect, useLocation, useNavigate } from "react-router-dom";
import rightIcon from "../../../img/rightImgIcon.png";
import notImportantIcon from "../../../img/not-important.svg";
import { useNavigation } from "../../../Contexts/navigationContext";
import { placeOrderAndShowRz, useInitiateSubscription } from "../../../api/subscriptionPlan.api";
import { usePlansProvider } from "../../../Contexts/plansContext";
import Loader from "../../Loader/Loader";
import { useGlobalContext } from "../../../Contexts/GlobalContext";
import { getCurrentCourse } from "../../../courses/CourseUtility";
import { redirectToDashboardWithCorrectCourseHighlighted } from "../../../Utilities/ReRoutingUtils";

// Mobile shows 12 Months > 6 Months > 1 Month; desktop keeps the natural array order.
const MOBILE_ORDER_CLASS = {
    1: "order-3 md:order-1",
    2: "order-2 md:order-2",
    3: "order-1 md:order-3",
};

const plans = [
    {
        id: 1,
        title: "Starter Pack",
        duration: "3 Months",
        oldPrice: "₹9,999",
        price: "₹8,999",
        priceList: "which costs ₹2,999/month",
        buttonText: "Subscribe Now",
        features: [
            { bold: "100+", normal: " Study Hrs Effort Per week" },
            { bold: "500+", normal: " Concept videos" },
            { bold: "600+", normal: " Handpicked Problems" },
            { bold: "24*7", normal: " Doubt Support" },
            { bold: "", normal: " 12 Days of Free Subscription Pause" },
            { bold: "", normal: " Performance Based Mock Interviews", notImportant: true },
            { bold: "", normal: " Performance based Referrals", notImportant: true },
        ],
    },
    {
        id: 2,
        title: "Most Popular",
        duration: "6 Months",
        oldPrice: "₹19,999",
        price: "₹15,679",
        priceList: "which costs ₹2,613/month",
        buttonText: "Subscribe Now",
        features: [
            { bold: "20-24", normal: " Study Hrs Effort Per week" },
            { bold: "500+", normal: " Concept videos" },
            { bold: "600+", normal: " Handpicked Problems" },
            { bold: "24*7", normal: " Doubt Support" },
            { bold: "", normal: "45 Days of Free Subscription Pause" },
            { bold: "", normal: "Performance Based Mock Interviews" },
            { bold: "", normal: "Performance based Referrals" },
        ],
    },
    {
        id: 3,
        title: "Best Value",
        duration: "12 Months",
        oldPrice: "₹29,999",
        price: "₹23,999",
        priceList: "which costs ₹1,999/month",
        buttonText: "Subscribe Now",
        features: [
            { bold: "10-12", normal: " Study Hrs Effort Per week" },
            { bold: "500+", normal: " Concept videos" },
            { bold: "600+", normal: " Handpicked Problems" },
            { bold: "24*7", normal: " Doubt Support" },
            { bold: "", normal: "90 Days of Free Subscription Pause" },
            { bold: "", normal: "Performance Based Mock Interviews" },
            { bold: "", normal: "Performance based Referrals" },
        ],
    },
];

const ArrowRightIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M4.16667 10H15.8333M15.8333 10L10.8333 5M15.8333 10L10.8333 15"
            stroke="#F8FAFC"
            strokeWidth="1.67"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const Plans = () => {
    const initiateSubscription = useInitiateSubscription();
    const { plans: fetchPlans, couponCode } = usePlansProvider();
    const { plansRef } = useNavigation();
    const navigate = useNavigate();
    const [isShowGlobalLoader, setIsShowGlobalLoader] = useState(false);
    const { loggedIn, onFreeTrial, subscribed, everSubscribed, currentLoggedInUserEmail } =
        useGlobalContext();

    const location = useLocation();
    const url = location.pathname;

    const handleSubscriptionPlan = (subscription) => {
        console.log("Navigate to dashboard: ", subscribed, onFreeTrial);
        if (!loggedIn) navigate("/signup");
        else if (!everSubscribed) {
            redirectToDashboardWithCorrectCourseHighlighted(url);
        } else if (subscribed && !onFreeTrial) {
            window.open(
                `${
                    process.env.REACT_APP_NEW_DASHBOARD_URL
                }/${getCurrentCourse()}/classroom/manage-plan`,
                "_blank",
            );
        } else {
            setIsShowGlobalLoader(true);
            initiateSubscription.mutate(
                {
                    subscriptionID: subscription.subscription_id,
                    username: currentLoggedInUserEmail,
                    couponCode: couponCode,
                },
                {
                    onSuccess(res) {
                        const apiRes = res.Response;
                        const { orderId, key, amount, currency } = apiRes;
                        placeOrderAndShowRz(
                            currentLoggedInUserEmail,
                            orderId,
                            key,
                            amount,
                            currency,
                        );
                        setIsShowGlobalLoader(false);
                    },
                    onError() {
                        setIsShowGlobalLoader(false);
                    },
                },
            );
        }
    };

    const getMatchingPlan = (duration) => {
        return fetchPlans.find((plan) => plan.subscription_name?.trim() === duration?.trim());
    };

    return (
        <div
            className="bg-[rgba(24,27,67,1)] px-5 py-10 md:px-[120px] md:py-[60px] flex flex-col items-center gap-10"
            ref={plansRef}
        >
            <div className="flex flex-col items-center gap-5">
                <p className="font-gilroy-bold border-2 border-[#FBBF24] text-[#FBBF24] text-[16px] leading-6 tracking-[0.02em] rounded-[47px] w-fit px-6 py-1 md:text-[24px] md:leading-[30px] md:px-9 md:py-2 text-center">
                    Pricing
                </p>
                <p className="font-gilroy-bold text-[24px] leading-[32px] tracking-[0.02em] md:text-[40px] md:leading-[52px] md:tracking-[0.04em] text-white text-center">
                    Pricing Plans for You
                </p>
            </div>

            <div className="flex flex-col md:flex-row md:items-end gap-8 md:gap-6 w-full max-w-[1200px]">
                {plans.map((elem, index) => {
                    const matchingPlan = getMatchingPlan(elem.duration);
                    const durationMonths =
                        parseInt(elem.duration) ||
                        (elem.duration.includes("12") ? 12 : elem.duration.includes("6") ? 6 : 1);
                    const mrp = Number(matchingPlan?.mrp) || 0;
                    const discount = Number(matchingPlan?.discount) || 0;
                    const discountedPrice = Math.floor(mrp - (mrp * discount) / 100);
                    const isBestValue = elem.title === "Best Value";

                    const pauseDaysFeature = elem.features.find((f) =>
                        /free s?ubscription pause/i.test(f.normal),
                    );
                    const pauseDays = pauseDaysFeature?.normal.match(/\d+/)?.[0];

                    const cardContent = (
                        <div className="bg-white rounded-[20px] p-5 w-full md:min-h-[558px] flex flex-col items-start gap-5">
                            <div className="flex items-start justify-between gap-3 w-full">
                                <p className="font-gilroy-bold text-[20px] leading-7 tracking-[0.02em] uppercase text-[#020617]">
                                    {elem.duration}
                                </p>
                                {discount > 0 && (
                                    <div className="shrink-0 bg-[#FEF3C7] border border-[#D97706] text-[#D97706] rounded-full px-2 py-1 font-gilroy-bold text-[16px] leading-[22px] whitespace-nowrap">
                                        Save: ₹
                                        {Intl.NumberFormat("en-IN").format(
                                            Math.floor((mrp * discount) / 100),
                                        )}
                                    </div>
                                )}
                            </div>

                            {matchingPlan ? (
                                <>
                                    <div className="flex items-center gap-3">
                                        <p className="font-gilroy-bold text-[32px] leading-10 tracking-[0.04em] text-[#020617]">
                                            ₹
                                            {Intl.NumberFormat("en-IN").format(
                                                discount > 0 ? discountedPrice : mrp,
                                            )}
                                        </p>
                                        {discount > 0 && (
                                            <p className="font-gilroy-medium text-[20px] leading-7 tracking-[0.02em] text-[#94A3B8] line-through">
                                                ₹{Intl.NumberFormat("en-IN").format(mrp)}
                                            </p>
                                        )}
                                    </div>

                                    {pauseDays && (
                                        <div className="flex items-center gap-2">
                                            <p className="font-gilroy-bold text-[16px] leading-[22px] tracking-[0.02em] text-[#4659CA]">
                                                + {pauseDays} Free Pause Days
                                            </p>
                                        </div>
                                    )}

                                    <p className="font-gilroy-bold text-[16px] leading-[22px] tracking-[0.02em] text-[#231F20]">
                                        which costs ₹
                                        {Intl.NumberFormat("en-IN").format(
                                            discount > 0
                                                ? Math.round(discountedPrice / durationMonths)
                                                : Math.round(mrp / durationMonths),
                                        )}
                                        /month
                                    </p>
                                </>
                            ) : (
                                <div className="w-full flex items-center justify-start">
                                    <div className="animate-pulse space-y-2 flex flex-col items-start">
                                        <div className="h-8 w-40 bg-gray-300 rounded"></div>
                                        <div className="h-4 w-32 bg-gray-300 rounded"></div>
                                        <div className="h-4 w-48 bg-gray-300 rounded"></div>
                                    </div>
                                </div>
                            )}

                            <button
                                className="w-full flex items-center justify-center gap-1 py-3 px-7 rounded-2xl bg-gradient-to-b from-[#4659CA] to-[#3940A0] shadow-[0px_14px_4px_-4px_rgba(0,0,0,0.25),inset_0px_0.62px_1.11px_#FFFFFF,inset_0px_-1.24px_0px_#000000] text-white font-gilroy-bold text-[16px] leading-[22px] tracking-[0.02em] hover:opacity-90 transition"
                                onClick={() => handleSubscriptionPlan(matchingPlan)}
                            >
                                {elem.buttonText}
                                <ArrowRightIcon />
                            </button>

                            <div className="text-left text-[16px] flex flex-col gap-3 w-full">
                                <p className="font-gilroy-bold text-[16px] leading-[22px] tracking-[0.02em] text-[#191D23]">
                                    Features
                                </p>
                                {elem.features.map((feature, fIndex) => (
                                    <div key={fIndex} className="flex items-center gap-2">
                                        <img
                                            src={
                                                feature?.notImportant ? notImportantIcon : rightIcon
                                            }
                                            alt="icon"
                                            width={24}
                                            height={24}
                                        />
                                        <p className="font-gilroy-medium text-[16px] leading-[22px] tracking-[0.02em] text-[#231F20]">
                                            {feature.bold && (
                                                <span className="text-[rgba(68,75,171,1)]">
                                                    {feature.bold}
                                                </span>
                                            )}

                                            {feature.normal
                                                ?.toLowerCase()
                                                .includes("free subscription") ||
                                            feature.normal
                                                ?.toLowerCase()
                                                .includes("free sbubscription") ? (
                                                <>
                                                    {feature.normal
                                                        .split(
                                                            /(Free Subscription|Free Sbubscription)/,
                                                        )
                                                        .map((part, i) =>
                                                            [
                                                                "Free Subscription",
                                                                "Free Sbubscription",
                                                            ].includes(part) ? (
                                                                <span
                                                                    key={i}
                                                                    className="text-[rgba(68,75,171,1)]"
                                                                >
                                                                    {part}
                                                                </span>
                                                            ) : (
                                                                part
                                                            ),
                                                        )}
                                                </>
                                            ) : (
                                                <span
                                                    className={`${
                                                        feature?.notImportant && "text-[#64748B]"
                                                    }`}
                                                >
                                                    {feature.normal}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );

                    return isBestValue ? (
                        <div
                            key={elem.id}
                            className={`w-full md:w-[384px] md:h-[608px] md:shrink-0 bg-[#FDE68A] rounded-[20px] pt-3 px-2 pb-2 flex flex-col items-center gap-2 ${MOBILE_ORDER_CLASS[elem.id]}`}
                        >
                            <p className="font-gilroy-bold text-[16px] leading-[22px] tracking-[0.02em] text-center text-[#020617]">
                                Best Value
                            </p>
                            {cardContent}
                        </div>
                    ) : (
                        <div
                            key={elem.id}
                            className={`w-full md:w-[384px] md:shrink-0 ${MOBILE_ORDER_CLASS[elem.id]}`}
                        >
                            {cardContent}
                        </div>
                    );
                })}
            </div>
            {isShowGlobalLoader && <Loader />}
        </div>
    );
};

export default Plans;
